import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Modal } from '../../../components/Modal';
import { vmsService } from '../../../services/vmsService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  vendorId: string;
  entry: any;
  onEdit: () => void;
}

export const DocVersionsModal: React.FC<Props> = ({ isOpen, onClose, vendorId, entry, onEdit }) => {
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [shareVersionId, setShareVersionId] = useState<number | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isFinanceDept = currentUser?.department?.toLowerCase() === 'finance';
  const canDownload = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  useEffect(() => {
    if (!isOpen || !entry) return;
    setLoading(true);
    vmsService.getVendorDocVersions(vendorId, entry.id)
      .then(data => {
        const sorted = [...data].sort((a, b) => b.version_number - a.version_number);
        setVersions(sorted);
      })
      .catch(err => console.error('Failed to fetch versions', err))
      .finally(() => setLoading(false));
  }, [isOpen, vendorId, entry]);

  const handleDownload = async (versionId: number) => {
    try {
      const res = await vmsService.downloadVendorDocVersion(vendorId, entry.id, versionId);
      if (res?.url) window.open(res.url, '_blank');
    } catch (err) {
      alert('Failed to generate download link.');
    }
  };

  const openShareModal = (versionId: number) => {
    setShareVersionId(versionId);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const closeShareModal = () => {
    setShareVersionId(null);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) {
      setShareError('Please enter a recipient email.');
      return;
    }
    setShareLoading(true);
    setShareError(null);
    try {
      await vmsService.shareVendorDocVersion(vendorId, entry.id, shareVersionId!, shareEmail.trim());
      setShareSuccess(true);
    } catch (err: any) {
      setShareError(err.message || 'Failed to send email.');
    } finally {
      setShareLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Document Details — ${entry.doc_type}`}>
        <div className="modal-body">
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '1.05rem', color: 'var(--text-dark)' }}><strong>Document Type:</strong> {entry.doc_type}</p>
          </div>

          <h3 className="section-subtitle">Version History ({versions.length})</h3>

          {loading ? (
            <p className="text-muted">Loading versions...</p>
          ) : versions.length === 0 ? (
            <p className="text-muted">No file versions uploaded yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="data-table data-table-sm">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Uploaded By</th>
                    <th>Date</th>
                    <th>Base Rate at Upload</th>
                    <th>Payment Details</th>
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.id}>
                      <td>
                        <span className="version-number">v{v.version_number}</span>
                        {v.is_current && (
                          <span className="status-badge status-active version-badge">Current</span>
                        )}
                      </td>
                      <td>{v.uploaded_by || '—'}</td>
                      <td>{new Date(v.uploaded_at).toLocaleString()}</td>
                      <td>{v.active_base_rate ? `${v.active_base_rate}%` : '—'}</td>
                      <td>
                        {v.amount_paid ? (
                          <div style={{ fontSize: '0.85rem' }}>
                            <div><strong>Amt:</strong> ₹{v.amount_paid}</div>
                            {v.payment_date && isFinanceDept && <div><strong>Date:</strong> {v.payment_date}</div>}
                            {v.utr_number && <div><strong>UTR:</strong> {v.utr_number}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td>{v.remarks || '—'}</td>
                      <td>
                        <div className="action-cell">
                          <button className="secondary-btn sm-btn" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          {canDownload && v.s3_url && (
                            <button className="secondary-btn sm-btn" onClick={() => handleDownload(v.id)} title="Download">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download
                            </button>
                          )}
                          {v.s3_url && (
                            <button
                              className="secondary-btn sm-btn"
                              onClick={() => openShareModal(v.id)}
                              title="Share via email"
                            >
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Share
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>Close</button>
        </div>
      </Modal>

      {shareVersionId !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeShareModal(); }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '380px',
              boxShadow: '0 8px 32px rgba(71, 49, 146, 0.18)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                Share File
              </h3>
              <button
                onClick={closeShareModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px' }}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
              Sharing <strong style={{ color: '#111827' }}>{entry.doc_type}</strong> — v{versions.find(v => v.id === shareVersionId)?.version_number}
            </p>

            {shareSuccess ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: '#f5f3ff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 0.75rem'
                }}>
                  <svg fill="none" stroke="#473192" viewBox="0 0 24 24" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#374151', fontWeight: 600 }}>
                  Email sent successfully!
                </p>
                <button className="secondary-btn" onClick={closeShareModal}>Done</button>
              </div>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                  Recipient Email
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
                  style={{
                    width: '100%',
                    padding: '0.55rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    marginBottom: shareError ? '0.5rem' : '1rem',
                  }}
                  autoFocus
                />
                {shareError && (
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#dc2626' }}>
                    {shareError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="secondary-btn" onClick={closeShareModal} disabled={shareLoading}>
                    Cancel
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={shareLoading}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: shareLoading ? '#9ca3af' : '#473192',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: shareLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {shareLoading ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
